import React, { useState, useEffect, useCallback, useRef } from 'react';
import StorageGauge from './StorageGauge';
import { apiFetch } from '../api';
const PAGE_SIZE = 30;

const FLAG_OPTIONS = [
  { value: 'keep', label: 'Keep', color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-100' },
  { value: 'archive', label: 'Archive', color: 'bg-amber-500', textColor: 'text-amber-700', bgColor: 'bg-amber-100' },
  { value: 'delete', label: 'Delete', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-100' },
  { value: 'unreviewed', label: 'Unreviewed', color: 'bg-slate-300', textColor: 'text-slate-600', bgColor: 'bg-slate-100' },
];

function FlagBadge({ flag }) {
  const opt = FLAG_OPTIONS.find(f => f.value === flag) || FLAG_OPTIONS[3];
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${opt.textColor} ${opt.bgColor}`}>
      {opt.label}
    </span>
  );
}

function FlagSelector({ value, onChange, compact }) {
  if (compact) {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-slate-300 rounded px-2 py-1 text-xs"
      >
        {FLAG_OPTIONS.map(f => (
          <option key={f.value} value={f.value}>{f.label}</option>
        ))}
      </select>
    );
  }
  return (
    <div className="flex gap-1">
      {FLAG_OPTIONS.filter(f => f.value !== 'unreviewed').map(f => (
        <button
          key={f.value}
          onClick={() => onChange(f.value)}
          className={`px-2 py-1 rounded text-xs font-medium transition ${
            value === f.value
              ? `${f.bgColor} ${f.textColor} ring-2 ring-offset-1 ring-${f.value === 'keep' ? 'green' : f.value === 'archive' ? 'amber' : 'red'}-300`
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
          }`}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}


const RECOMMENDATION_COLORS = {
  keep:    { textColor: 'text-green-700' },
  archive: { textColor: 'text-amber-700' },
  delete:  { textColor: 'text-red-700'   },
};

function RecommendationBadge({ recommendation }) {
  const opt = FLAG_OPTIONS.find(f => f.value === recommendation.flag);
  const label = opt ? opt.label : recommendation.flag;
  const { textColor } = RECOMMENDATION_COLORS[recommendation.flag] || { textColor: 'text-slate-700' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium
      border border-dashed border-purple-300 bg-purple-50 ${textColor} ml-1`}>
      ✦ {label}
    </span>
  );
}


export default function RationalizationWorkbench({ onUnreviewedCountChange }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);

  // Filters
  const [filterFlag, setFilterFlag] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterFolder, setFilterFolder] = useState('all');
  const [searchText, setSearchText] = useState('');

  // Bulk action state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(null);

  // Bulk flag by file type (dynamic — no hardcoded extensions)
  const [bulkTypeExt, setBulkTypeExt] = useState('');
  const [bulkTypeAction, setBulkTypeAction] = useState('archive');

  // Export state
  const [exported, setExported] = useState(null);

  // Apply Flags state: null = never applied, {applied_at, summary} = applied
  const [applied, setApplied] = useState(null);
  // Stale: true when flags changed after the last Apply
  const [applyStale, setApplyStale] = useState(false);

  // AI analysis state
  const [analysing, setAnalysing] = useState(false);
  const [showAnalyseConfirm, setShowAnalyseConfirm] = useState(false);
  const [analysisEstimate, setAnalysisEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState({ processed: 0, total: 0, skipped: 0 });
  const [allFlagged, setAllFlagged] = useState(false);
  const [fileSizeUnit, setFileSizeUnit] = useState('bytes');

  // Ref for polling interval — use ref (not state) so clearInterval inside the
  // interval callback always sees the current ID without stale closure issues.
  const analysisIntervalRef = useRef(null);
  const initializedRef = useRef(false);

  // Clear polling interval on unmount
  useEffect(() => {
    return () => {
      if (analysisIntervalRef.current) clearInterval(analysisIntervalRef.current);
    };
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      if (!initializedRef.current) {
        const initRes = await apiFetch(`/rationalization/init`, { method: 'POST' });
        if (!initRes.ok) throw new Error('Could not initialize asset review');
        initializedRef.current = true;
      }
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
        flag: filterFlag,
        extension: filterType,
        folder: filterFolder,
        search: searchText,
      });
      const res = await apiFetch(`/rationalization/summary?${params.toString()}`);
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.message || data.error || 'Could not load asset review');
      setSummary(data);
      // Restore apply state from persisted rationalization.json
      if (data.flags_applied_at) {
        setApplied({ applied_at: data.flags_applied_at, summary: data.applied_summary });
      } else {
        setApplied(null);
      }
      setApplyStale(false);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [page, filterFlag, filterType, filterFolder, searchText]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Lift unreviewed count to App whenever summary changes
  useEffect(() => {
    if (!onUnreviewedCountChange) return;
    const count = summary?.by_flag?.unreviewed?.count ?? 0;
    onUnreviewedCountChange(count);
  }, [summary, onUnreviewedCountChange]);

  const handleFlag = async (assetId, flag) => {
    try {
      const response = await apiFetch(`/rationalization/flag`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_id: assetId, flag }),
      });
      if (!response.ok) throw new Error('Could not update this asset');
      await loadSummary();
      // Mark apply checkpoint stale — flags changed since last Apply
      if (applied) setApplyStale(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleBulkFlag = async (flag) => {
    if (selectedIds.size === 0) return;

    // Use the atomic bulk-flag endpoint with an explicit ID list.
    // This is a single read-modify-write on the server — safe under concurrent load.
    // Sending N individual /flag requests in parallel risks last-writer-wins overwrites
    // when multiple Gunicorn workers handle requests simultaneously.
    try {
      const res = await apiFetch(`/rationalization/bulk-flag`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filter_rules: { ids: Array.from(selectedIds) },
          flag,
          reason: 'Manual selection',
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.message || data.error || 'Failed to flag assets');
      setSelectedIds(new Set());
      setBulkConfirm(null);
      loadSummary();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleQuickFlag = async (filterRules, flag, reason) => {
    try {
      const res = await apiFetch(`/rationalization/bulk-flag`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filter_rules: filterRules, flag, reason }),
      });
      const data = await res.json();
      setBulkConfirm(null);
      loadSummary();
      return data.affected;
    } catch (err) {
      setError(err.message);
    }
  };

  const handleConfirmFileSizeUnit = async () => {
    try {
      const response = await apiFetch('/rationalization/file-size-unit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unit: fileSizeUnit }),
      });
      const data = await response.json();
      if (!response.ok || data.error) throw new Error(data.error || data.message || 'Could not apply file-size unit');
      await loadSummary();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExport = async () => {
    try {
      const res = await apiFetch(`/rationalization/export`, { method: 'POST' });
      const data = await res.json();
      setExported(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleApplyFlags = async () => {
    try {
      const res = await apiFetch(`/rationalization/apply`, { method: 'POST' });
      const data = await res.json();
      setApplied({ applied_at: data.flags_applied_at, summary: data.applied_summary });
      setApplyStale(false);
    } catch (err) {
      setError(err.message);
    }
  };

  // startPolling: begins a 2-second setInterval that reads analysis_progress.json.
  // Extracted so it can be called from handleAnalyse (after POST) and from the
  // mount-check effect (if analysis was already running when component loaded).
  const startPolling = useCallback(() => {
    if (analysisIntervalRef.current) return; // already polling — don't double up

    analysisIntervalRef.current = setInterval(async () => {
      try {
        const res = await apiFetch(`/rationalization/analyse/progress`);
        if (!res.ok) return; // transient network error — keep polling
        const progress = await res.json();

        if (progress.status === 'running' || progress.status === 'starting') {
          setAnalysisProgress({
            processed: progress.processed || 0,
            total: progress.total || 0,
            skipped: progress.skipped || 0,
          });
          return; // still going — next tick
        }

        // Terminal states — stop polling
        clearInterval(analysisIntervalRef.current);
        analysisIntervalRef.current = null;

        if (progress.status === 'complete') {
          setAnalysisProgress({
            processed: progress.processed || 0,
            total: progress.total || 0,
            skipped: progress.skipped || 0,
          });
          setAnalysing(false);
          loadSummary(); // Refresh to pick up all recommendations
        } else if (progress.status === 'all_flagged') {
          setAllFlagged(true);
          setAnalysing(false);
        } else if (progress.status === 'cancelled') {
          setAnalysisProgress({
            processed: progress.processed || 0,
            total: progress.total || 0,
            skipped: progress.skipped || 0,
          });
          setAnalysing(false);
          loadSummary(); // Pick up any partial recommendations
        } else if (progress.status === 'error') {
          setError(progress.message || 'Analysis failed.');
          setAnalysing(false);
        }
        // 'idle' with no thread running — shouldn't happen after a successful start,
        // but handle gracefully by stopping the poll without setting an error.
        else if (progress.status === 'idle') {
          setAnalysing(false);
        }
      } catch (err) {
        // Network error polling progress — don't stop; next tick will retry.
        console.warn('[analyse] Progress poll failed:', err.message);
      }
    }, 2000);
  }, [loadSummary]);

  // On mount: if analysis was already running (e.g. user navigated away and came back),
  // resume the progress display and polling without restarting the analysis.
  useEffect(() => {
    apiFetch(`/rationalization/analyse/progress`)
      .then(r => r.json())
      .then(progress => {
        if (progress.status === 'running' || progress.status === 'starting') {
          setAnalysing(true);
          setAnalysisProgress({
            processed: progress.processed || 0,
            total: progress.total || 0,
            skipped: progress.skipped || 0,
          });
          startPolling();
        }
      })
      .catch(() => {}); // Ignore — analysis simply not running
  }, [startPolling]);

  const openAnalyseEstimate = useCallback(async (force = false) => {
    setEstimating(true);
    setError(null);
    try {
      const response = await apiFetch('/rationalization/analyse/estimate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force }),
      });
      const estimate = await response.json();
      if (!response.ok || estimate.error) throw new Error(estimate.message || estimate.error || 'Could not estimate analysis');
      setAnalysisEstimate(estimate);
      setShowAnalyseConfirm(true);
    } catch (err) {
      setError(err.message);
    }
    setEstimating(false);
  }, []);

  const handleAnalyse = useCallback((force = false) => {
    if (!analysisEstimate) return;
    setShowAnalyseConfirm(false);
    setAllFlagged(false);
    setAnalysing(true);
    setAnalysisProgress({ processed: 0, total: 0, skipped: 0 });
    setError(null);

    // POST to start the background thread, then poll for progress.
    // The AI request runs in a daemon thread, not in the HTTP handler,
    // so Gunicorn's worker timeout cannot kill it mid-analysis.
    apiFetch(`/rationalization/analyse`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ force, approved_estimate_id: analysisEstimate.estimate_id }),
    })
      .then(r => {
        if (!r.ok) return r.json().then(d => { throw new Error(d.message || 'Failed to start analysis'); });
        return r.json();
      })
      .then(() => {
        startPolling();
      })
      .catch(err => {
        setError(err.message);
        setAnalysing(false);
      });
  }, [startPolling, analysisEstimate]);

  const handleCancelAnalyse = useCallback(async () => {
    // Stop the local polling interval immediately
    if (analysisIntervalRef.current) {
      clearInterval(analysisIntervalRef.current);
      analysisIntervalRef.current = null;
    }
    // Signal the background thread to stop after its current batch
    try {
      await apiFetch(`/rationalization/analyse/cancel`, { method: 'POST' });
    } catch {
      // Ignore cancel errors — the UI is already updated
    }
    setAnalysing(false);
  }, []);

  const handleAcceptRecommendation = async (assetId) => {
    try {
      await apiFetch(`/rationalization/accept-recommendation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_id: assetId }),
      });
      setSummary(prev => {
        if (!prev) return prev;
        const asset = prev.assets.find(a => a.id === assetId);
        if (!asset?.recommendation) return prev;
        const newFlag = asset.recommendation.flag;
        const assets = prev.assets.map(a =>
          a.id === assetId ? { ...a, flag: newFlag, recommendation: null } : a
        );
        const by_flag = {};
        FLAG_OPTIONS.forEach(f => { by_flag[f.value] = { count: 0, bytes: 0 }; });
        assets.forEach(a => {
          by_flag[a.flag].count += 1;
          by_flag[a.flag].bytes += (a.size_bytes || 0);
        });
        const recommendation_count = Math.max(0, (prev.recommendation_count || 1) - 1);
        return { ...prev, assets, by_flag, recommendation_count };
      });
      if (applied) setApplyStale(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAcceptAll = async () => {
    setBulkConfirm(null);
    try {
      const res = await apiFetch(`/rationalization/accept-all-recommendations`, { method: 'POST' });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      if (applied) setApplyStale(true);
      // Reset suggestion filter — no suggestions will remain after accept-all
      if (filterFlag === 'has_suggestion') setFilterFlag('all');
      loadSummary();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleClearRecommendations = async () => {
    setBulkConfirm(null);
    try {
      await apiFetch(`/rationalization/clear-recommendations`, { method: 'POST' });
      // Reset suggestion filter — no suggestions will remain after clear
      if (filterFlag === 'has_suggestion') setFilterFlag('all');
      loadSummary();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFlagDuplicates = async () => {
    setBulkConfirm(null);
    setError(null);
    try {
      const res = await apiFetch(`/rationalization/flag-duplicates`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || data.message || 'Failed to flag duplicates');
      loadSummary();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-lg font-medium text-slate-700">Loading asset data for review...</div>
      </div>
    );
  }

  if (!summary) return null;

  const duplicateStats = {
    exactGroupCount: summary.duplicate_stats?.exact_group_count || 0,
    exactAssetCount: summary.duplicate_stats?.exact_asset_count || 0,
    archiveCandidateCount: summary.duplicate_stats?.archive_candidate_count || 0,
    conflictGroupCount: summary.duplicate_stats?.filename_conflict_group_count || 0,
    conflictAssetCount: summary.duplicate_stats?.filename_conflict_asset_count || 0,
  };
  const extensions = summary.extensions || [];
  const folders = summary.folders || [];
  const totalPages = summary.pagination?.total_pages || 0;
  const pageItems = summary.assets || [];

  // Select all on current page
  const allOnPageSelected = pageItems.length > 0 && pageItems.every(a => selectedIds.has(a.id));

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        pageItems.forEach(a => next.delete(a.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        pageItems.forEach(a => next.add(a.id));
        return next;
      });
    }
  };

  // Derive Apply button state
  const isApplied = applied && !applyStale;
  const isStale = applied && applyStale;
  const appliedTime = applied
    ? new Date(applied.applied_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  const unreviewedNow = summary.by_flag?.unreviewed?.count ?? 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Rationalize Assets</h2>
          <p className="text-sm text-slate-500 mt-1">
            Flag each asset as Keep, Archive, or Delete, then apply your decisions.
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <button
            onClick={handleApplyFlags}
            disabled={isApplied}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
              isApplied
                ? 'bg-green-100 text-green-700 cursor-default'
                : isStale
                ? 'bg-amber-500 text-white hover:bg-amber-600'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isApplied
              ? `Flags Applied ✓  ${appliedTime}`
              : isStale
              ? 'Re-apply Flags'
              : 'Apply Flags'}
          </button>
        </div>
      </div>

      {/* Flag definitions — always visible so clients understand the consequences of each flag */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4 text-sm">
        <p className="font-semibold text-slate-700 mb-2">What do these flags mean?</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="flex gap-2">
            <span className="mt-0.5 h-3 w-3 rounded-full bg-green-500 shrink-0"></span>
            <div>
              <span className="font-medium text-green-700">Keep</span>
              <p className="text-slate-500 text-xs mt-0.5">Asset will be included in the Frontify ingestion file and migrated.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <span className="mt-0.5 h-3 w-3 rounded-full bg-amber-500 shrink-0"></span>
            <div>
              <span className="font-medium text-amber-700">Archive</span>
              <p className="text-slate-500 text-xs mt-0.5">Asset will <strong>not</strong> be migrated to Frontify but should be retained in your source system (e.g. for legal or compliance reasons).</p>
            </div>
          </div>
          <div className="flex gap-2">
            <span className="mt-0.5 h-3 w-3 rounded-full bg-red-500 shrink-0"></span>
            <div>
              <span className="font-medium text-red-700">Delete</span>
              <p className="text-slate-500 text-xs mt-0.5">Asset will <strong>not</strong> be migrated and is flagged for permanent destruction. Use only where you have confirmed no retention obligation exists.</p>
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-3">Neither Archive nor Delete assets appear in the Frontify export.</p>
      </div>

      {/* Apply Flags summary panel */}
      {applied && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          {applyStale ? (
            <div className="text-sm text-amber-700 font-medium mb-1">
              ⚠ Flags have changed since you last applied — click Re-apply Flags to update.
            </div>
          ) : (
            <div className="text-sm font-semibold text-blue-800 mb-1">
              Flags applied: <span className="font-normal">
                {applied.summary?.keep ?? 0} Keep ·{' '}
                {applied.summary?.archive ?? 0} Archive ·{' '}
                {applied.summary?.delete ?? 0} Delete ·{' '}
                {applied.summary?.unreviewed ?? 0} Unreviewed
              </span>
            </div>
          )}
          {!applyStale && unreviewedNow > 0 && (
            <div className="text-sm text-amber-700 mt-1">
              ⚠ <strong>{unreviewedNow.toLocaleString()} asset{unreviewedNow !== 1 ? 's' : ''} are still unreviewed</strong> and will be excluded from the export.
            </div>
          )}
          {!applyStale && unreviewedNow === 0 && (
            <div className="text-sm text-blue-700 mt-1">
              Your rationalization decisions are saved. Proceed to Export when ready.
            </div>
          )}
        </div>
      )}

      {/* Storage gauge */}
      {summary.file_size_unit_requires_confirmation && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex flex-wrap items-center gap-3 text-sm text-amber-900">
          <span className="font-medium">Confirm the unit used by the numeric file-size column before storage totals are calculated.</span>
          <select
            value={fileSizeUnit}
            onChange={event => setFileSizeUnit(event.target.value)}
            className="border border-amber-300 bg-white rounded px-2 py-1 text-sm"
          >
            <option value="bytes">Bytes</option>
            <option value="KB">KB</option>
            <option value="MB">MB</option>
            <option value="GB">GB</option>
          </select>
          <button
            onClick={handleConfirmFileSizeUnit}
            className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded hover:bg-amber-700"
          >
            Apply unit
          </button>
        </div>
      )}
      <StorageGauge storage={summary} />

      {/* Quick actions */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Quick Actions</h4>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setBulkConfirm({
              label: 'Flag all unreviewed assets as Keep',
              action: () => handleQuickFlag({ current_flag: 'unreviewed' }, 'keep', 'Bulk: default keep'),
            })}
            className="px-3 py-1.5 bg-green-50 text-green-700 text-xs font-medium rounded hover:bg-green-100 transition"
          >
            Keep all unreviewed
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-600 font-medium whitespace-nowrap">Bulk flag by file type:</span>
            <select
              value={bulkTypeExt}
              onChange={e => setBulkTypeExt(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1.5 text-xs text-slate-700"
            >
              <option value="">— choose type —</option>
              {extensions.map(ext => <option key={ext} value={ext}>{ext}</option>)}
            </select>
            <select
              value={bulkTypeAction}
              onChange={e => setBulkTypeAction(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1.5 text-xs text-slate-700"
            >
              <option value="keep">Keep</option>
              <option value="archive">Archive</option>
              <option value="delete">Delete</option>
            </select>
            <button
              disabled={!bulkTypeExt}
              onClick={() => {
                if (!bulkTypeExt) return;
                const actionLabel = bulkTypeAction.charAt(0).toUpperCase() + bulkTypeAction.slice(1);
                setBulkConfirm({
                  label: `Flag all ${bulkTypeExt} files as ${actionLabel}?`,
                  action: () => handleQuickFlag({ extension: bulkTypeExt }, bulkTypeAction, `Bulk flag by type: ${bulkTypeExt}`),
                  ...(bulkTypeAction === 'archive' ? { reminder: 'These assets will be retained but not migrated to Frontify.' } : {}),
                  ...(bulkTypeAction === 'delete' ? { reminder: 'These assets will be excluded from the export and flagged for destruction. Confirm retention obligations are clear.' } : {}),
                });
              }}
              className="px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-medium rounded hover:bg-slate-200 transition disabled:opacity-40"
            >
              Apply
            </button>
          </div>
          {duplicateStats.exactGroupCount > 0 ? (
            <button
              onClick={() => setBulkConfirm({
                label: `Recommend ${duplicateStats.archiveCandidateCount.toLocaleString()} extra ${duplicateStats.archiveCandidateCount === 1 ? 'copy' : 'copies'} from ${duplicateStats.exactGroupCount.toLocaleString()} exact-match ${duplicateStats.exactGroupCount === 1 ? 'group' : 'groups'} for Archive?`,
                reminder: 'One copy per group remains Keep. Nothing is excluded until you accept the recommendations.',
                confirmLabel: 'Create recommendations',
                action: handleFlagDuplicates,
              })}
              className="px-3 py-1.5 bg-amber-50 text-amber-700 text-xs font-medium rounded hover:bg-amber-100 transition"
            >
              Recommend {duplicateStats.archiveCandidateCount.toLocaleString()} exact {duplicateStats.archiveCandidateCount === 1 ? 'duplicate' : 'duplicates'} for Archive
            </button>
          ) : (
            <span className="px-3 py-1.5 text-xs text-slate-400 italic">No exact duplicate candidates found</span>
          )}
          {duplicateStats.conflictGroupCount > 0 && (
            <span className="px-3 py-1.5 text-xs text-blue-700 bg-blue-50 rounded">
              {duplicateStats.conflictGroupCount.toLocaleString()} repeated filename {duplicateStats.conflictGroupCount === 1 ? 'group needs' : 'groups need'} review — different or unknown sizes are not treated as duplicates ({duplicateStats.conflictAssetCount.toLocaleString()} assets)
            </span>
          )}
        </div>

        {/* AI analysis button */}
        <div className="flex items-center gap-2 mt-3 justify-end">
          {(summary.recommendation_count || 0) > 0 && !analysing && (
            <button
              onClick={() => setBulkConfirm({
                label: `Re-run the AI analysis? This will replace all ${summary.recommendation_count} existing suggestions.`,
                action: handleAnalyse,
              })}
              className="px-3 py-1.5 bg-slate-100 text-slate-500 text-xs font-medium rounded hover:bg-slate-200 transition"
            >
              Re-analyze / Clear suggestions
            </button>
          )}
          {analysing ? (
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                  <svg className="animate-spin h-3 w-3 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  {analysisProgress.total > 0
                    ? `Analyzing assets… ${analysisProgress.processed} of ${analysisProgress.total}${analysisProgress.skipped > 0 ? ` (${analysisProgress.skipped} already flagged, skipped)` : ''}`
                    : 'Starting analysis…'}
                </div>
                <button
                  onClick={handleCancelAnalyse}
                  className="px-2 py-1.5 bg-slate-200 text-slate-600 text-xs font-medium rounded hover:bg-slate-300 transition"
                  title="Stop analysis after the current batch completes"
                >
                  Cancel
                </button>
              </div>
              {analysisProgress.total > 0 && (
                <div className="w-48 h-1.5 bg-purple-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-500 rounded-full transition-all duration-300"
                    style={{ width: `${Math.round((analysisProgress.processed / analysisProgress.total) * 100)}%` }}
                  />
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => { setAllFlagged(false); openAnalyseEstimate(false); }}
              disabled={!summary.has_api_key || estimating}
              title={!summary.has_api_key ? 'AI is not configured — contact your administrator' : ''}
              className={`px-3 py-1.5 text-xs font-medium rounded transition ${
                summary.has_api_key
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              {estimating ? 'Estimating…' : '✦ Analyze with AI'}
            </button>
          )}
        </div>

        {/* All assets already flagged — offer force override */}
        {allFlagged && !analysing && (
          <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
            <p className="text-sm text-purple-900 font-medium mb-1">All assets already have confirmed flags — nothing to analyze.</p>
            <p className="text-xs text-purple-700 mb-3">
              Run analysis only if you want AI to review your existing decisions and suggest changes.
            </p>
            <button
              onClick={() => openAnalyseEstimate(true)}
              className="px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700"
            >
              Analyze anyway
            </button>
          </div>
        )}

        {/* Analysis confirmation dialog */}
        {showAnalyseConfirm && (
          <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
            <p className="text-sm text-purple-900 font-medium mb-1">Review paid AI analysis estimate</p>
            {analysisEstimate && (
              <div className="text-xs text-purple-800 mb-3 space-y-1">
                <p><strong>{analysisEstimate.eligible_assets.toLocaleString()}</strong> eligible assets · {analysisEstimate.request_count.toLocaleString()} requests</p>
                <p>Uses the configured economical AI model</p>
                <p>{analysisEstimate.estimated_input_tokens.toLocaleString()} estimated input tokens · {analysisEstimate.estimated_output_tokens.toLocaleString()} estimated output tokens</p>
                <p>Estimated cost: <strong>${analysisEstimate.estimated_cost_usd.toFixed(2)}</strong> · Project budget: ${Number(analysisEstimate.budget_usd).toFixed(2)}</p>
                <p>Only metadata is sent. Suggestions do not change confirmed flags until you accept them.</p>
                {analysisEstimate.blockers?.map(blocker => (
                  <p key={blocker} className="text-red-700 font-medium">{blocker}</p>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => handleAnalyse(Boolean(analysisEstimate?.force))}
                disabled={!analysisEstimate?.can_run}
                className="px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                Run Analysis
              </button>
              <button
                onClick={() => { setShowAnalyseConfirm(false); setAnalysisEstimate(null); }}
                className="px-3 py-1 bg-white text-slate-600 text-xs font-medium rounded border hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Bulk confirmation dialog (shared) */}
        {bulkConfirm && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-amber-800 font-medium">{bulkConfirm.label}</p>
                {bulkConfirm.reminder && (
                  <p className="text-xs text-amber-700 mt-1">{bulkConfirm.reminder}</p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => bulkConfirm.action()}
                  className="px-3 py-1 bg-amber-600 text-white text-xs font-medium rounded hover:bg-amber-700"
                >
                  {bulkConfirm.confirmLabel || 'Confirm'}
                </button>
                <button
                  onClick={() => setBulkConfirm(null)}
                  className="px-3 py-1 bg-white text-slate-600 text-xs font-medium rounded border hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters + bulk bar */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <input
            type="text"
            placeholder="Search by filename..."
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
            className="border border-slate-300 rounded px-3 py-1.5 text-sm w-64"
          />
          <select value={filterFlag} onChange={(e) => { setFilterFlag(e.target.value); setPage(0); }}
            className="border border-slate-300 rounded px-3 py-1.5 text-sm">
            <option value="all">All flags</option>
            {FLAG_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            {(summary.recommendation_count || 0) > 0 && (
              <option value="has_suggestion">Has suggestion</option>
            )}
          </select>
          <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
            className="border border-slate-300 rounded px-3 py-1.5 text-sm">
            <option value="all">All types</option>
            {extensions.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <select value={filterFolder} onChange={(e) => { setFilterFolder(e.target.value); setPage(0); }}
            className="border border-slate-300 rounded px-3 py-1.5 text-sm">
            <option value="all">All folders</option>
            {folders.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <span className="text-xs text-slate-500 ml-auto">
            {(summary.total_matching || 0).toLocaleString()} of {summary.total_assets.toLocaleString()} assets
          </span>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-4">
            <span className="text-sm text-blue-800 font-medium">
              {selectedIds.size} asset{selectedIds.size > 1 ? 's' : ''} selected
            </span>
            <button onClick={() => setBulkConfirm({
              label: `Flag ${selectedIds.size} selected asset${selectedIds.size > 1 ? 's' : ''} as Keep`,
              action: () => handleBulkFlag('keep'),
            })} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200">Keep</button>
            <button onClick={() => setBulkConfirm({
              label: `Flag ${selectedIds.size} selected asset${selectedIds.size > 1 ? 's' : ''} as Archive`,
              reminder: 'These assets will be excluded from the Frontify export but should be retained in your source system.',
              action: () => handleBulkFlag('archive'),
            })} className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded hover:bg-amber-200">Archive</button>
            <button onClick={() => setBulkConfirm({
              label: `Flag ${selectedIds.size} selected asset${selectedIds.size > 1 ? 's' : ''} as Delete`,
              reminder: 'These assets will be excluded from the Frontify export and are flagged for permanent destruction. Ensure no retention obligations apply before proceeding.',
              action: () => handleBulkFlag('delete'),
            })} className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200">Delete</button>
            <button onClick={() => setSelectedIds(new Set())}
              className="text-xs text-slate-500 hover:text-slate-700 ml-auto">Clear selection</button>
          </div>
        )}
      </div>

      {/* AI recommendations banner */}
      {(summary.recommendation_count || 0) > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4 flex items-center justify-between">
          <div className="text-sm text-purple-900">
            <strong>✦ {summary.recommendation_count} AI suggestion{summary.recommendation_count !== 1 ? 's' : ''} ready</strong>
            {' '}— review suggestions in the table below, or accept all at once.
          </div>
          <div className="flex gap-2 ml-4 shrink-0">
            <button
              onClick={() => setBulkConfirm({
                label: `Accept all ${summary.recommendation_count} AI suggestions? This will update the confirmed flag for each asset.`,
                action: handleAcceptAll,
              })}
              className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 transition"
            >
              Accept all
            </button>
            <button
              onClick={handleClearRecommendations}
              className="px-3 py-1.5 bg-white text-purple-700 text-xs font-medium rounded border border-purple-300 hover:bg-purple-50 transition"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Unreviewed banner */}
      {(() => {
        const unreviewedCount = summary.by_flag?.unreviewed?.count ?? 0;
        return unreviewedCount > 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-sm text-amber-800">
            <strong>{unreviewedCount.toLocaleString()} asset{unreviewedCount !== 1 ? 's' : ''} are unreviewed</strong>
            {' '}— unreviewed assets will not be included in the export.
            Flag them Keep, Archive, or Delete to include or consciously exclude them.
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-sm text-green-800">
            ✓ All assets reviewed — ready to proceed to Export.
          </div>
        );
      })()}

      {/* Asset table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="py-2 px-3 text-left w-8">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={toggleSelectAll}
                  className="rounded"
                />
              </th>
              <th className="py-2 px-3 text-left text-xs font-semibold text-slate-500 uppercase">Filename</th>
              <th className="py-2 px-3 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
              <th className="py-2 px-3 text-left text-xs font-semibold text-slate-500 uppercase">Folder</th>
              <th className="py-2 px-3 text-left text-xs font-semibold text-slate-500 uppercase">Flag</th>
              <th className="py-2 px-3 text-left text-xs font-semibold text-slate-500 uppercase w-48">Action</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map(asset => (
              <tr
                key={asset.id}
                className="border-b border-slate-100 hover:bg-slate-50"
              >
                <td className={`py-2 px-3 ${asset.recommendation ? 'border-l-2 border-l-purple-300' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(asset.id)}
                    onChange={() => {
                      setSelectedIds(prev => {
                        const next = new Set(prev);
                        next.has(asset.id) ? next.delete(asset.id) : next.add(asset.id);
                        return next;
                      });
                    }}
                    className="rounded"
                  />
                </td>
                <td className="py-2 px-3 max-w-xs">
                  <div className="text-slate-800 font-medium truncate">{asset.filename}</div>
                  {asset.recommendation && (
                    <div className="text-xs text-purple-600 mt-0.5 truncate" title={asset.recommendation.reason}>
                      ✦ {asset.recommendation.reason}
                    </div>
                  )}
                </td>
                <td className="py-2 px-3 text-slate-500">{asset.extension || '—'}</td>
                <td className="py-2 px-3 text-slate-500 truncate max-w-[200px]">{asset.folder || '—'}</td>
                <td className="py-2 px-3">
                  <div className="flex flex-wrap items-center gap-1">
                    <FlagBadge flag={asset.flag} />
                    {asset.recommendation && (
                      <RecommendationBadge recommendation={asset.recommendation} />
                    )}
                  </div>
                </td>
                <td className="py-2 px-3">
                  {asset.recommendation ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleAcceptRecommendation(asset.id)}
                        className="px-2 py-1 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 transition whitespace-nowrap"
                      >
                        Accept
                      </button>
                      <FlagSelector
                        value={asset.flag}
                        onChange={(flag) => handleFlag(asset.id, flag)}
                        compact={true}
                      />
                    </div>
                  ) : (
                    <FlagSelector
                      value={asset.flag}
                      onChange={(flag) => handleFlag(asset.id, flag)}
                      compact={false}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="text-sm text-slate-600 hover:text-slate-800 disabled:text-slate-300 disabled:cursor-not-allowed">
              Previous
            </button>
            <span className="text-xs text-slate-500">Page {page + 1} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="text-sm text-slate-600 hover:text-slate-800 disabled:text-slate-300 disabled:cursor-not-allowed">
              Next
            </button>
          </div>
        )}
      </div>

      {/* Export */}
      <div className="flex justify-end">
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition"
        >
          Export Rationalization Report
        </button>
      </div>
      {exported && (
        <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 text-right">
          Report exported: {exported.row_count} assets written to rationalization_report.csv
        </div>
      )}

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
